import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobBoardAPI, cvAPI, aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ChevronLeft, Search, ExternalLink, Briefcase, MapPin,
  Loader2, Target, FileText, Sparkles, ArrowRight, CheckCircle,
  AlertCircle, TrendingUp, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const JobBoardPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [activeTab, setActiveTab] = useState('search');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  
  // Search form
  const [keywords, setKeywords] = useState('');
  const [location, setLocation] = useState('South Africa');
  
  // Match form
  const [cvs, setCvs] = useState([]);
  const [selectedCV, setSelectedCV] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [matchResult, setMatchResult] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);

  useEffect(() => {
    loadCVs();
  }, []);

  const loadCVs = async () => {
    try {
      const response = await cvAPI.list();
      setCvs(response.data);
      if (response.data.length > 0) {
        setSelectedCV(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load CVs');
    }
  };

  const handleSearch = async () => {
    if (!keywords.trim()) {
      toast.error('Please enter search keywords');
      return;
    }

    setLoading(true);
    try {
      const response = await jobBoardAPI.search({
        keywords: keywords.trim(),
        location: location
      });
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMatch = async () => {
    if (!selectedCV) {
      toast.error('Please select a CV');
      return;
    }
    if (!jobDescription.trim()) {
      toast.error('Please paste the job description');
      return;
    }
    if (!isSubscribed) {
      toast.error('Subscribe to use Job Matcher');
      navigate('/subscribe');
      return;
    }

    setMatchLoading(true);
    setMatchResult(null);
    
    try {
      const response = await aiAPI.matchJob(selectedCV, jobDescription);
      setMatchResult(response.data);
      toast.success('Analysis complete!');
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setMatchLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-100';
    if (score >= 60) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  const provinces = [
    'South Africa',
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Limpopo',
    'Mpumalanga',
    'North West',
    'Northern Cape'
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 text-white px-4 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-blue-200 hover:text-white hover:bg-blue-800 mb-4"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold">SA Job Board</h1>
              <p className="text-blue-200">Search jobs & match your CV instantly</p>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Selection */}
      <div className="bg-white border-b border-slate-200 px-4 lg:px-8 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('search')}
              className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'search'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Search className="w-4 h-4 inline mr-2" />
              Search Jobs
            </button>
            <button
              onClick={() => setActiveTab('match')}
              className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'match'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Target className="w-4 h-4 inline mr-2" />
              Match CV to Job
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="py-8 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          
          {/* Search Tab */}
          {activeTab === 'search' && (
            <div>
              {/* Search Form */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Find Jobs in South Africa</h2>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Input
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      placeholder="Job title, skills, or keywords..."
                      className="h-12"
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                  </div>
                  <Select value={location} onValueChange={setLocation}>
                    <SelectTrigger className="w-full sm:w-48 h-12">
                      <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {provinces.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleSearch}
                    disabled={loading}
                    className="h-12 px-8 bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Search className="w-5 h-5 mr-2" />
                        Search
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Search Results */}
              {searchResults && (
                <div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <p className="text-blue-800">
                      <Zap className="w-4 h-4 inline mr-1" />
                      <strong>Pro Tip:</strong> {searchResults.tip}
                    </p>
                  </div>

                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Search "{searchResults.keywords}" on these job boards:
                  </h3>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {searchResults.job_board_links.map((board, index) => (
                      <a
                        key={index}
                        href={board.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all group"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-2xl">{board.logo}</span>
                          <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                        <h4 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                          {board.name}
                        </h4>
                        <p className="text-sm text-slate-500 mt-1">View {searchResults.keywords} jobs</p>
                      </a>
                    ))}
                  </div>

                  <div className="mt-6 text-center">
                    <p className="text-slate-600 mb-4">Found a job you like? Check how well your CV matches:</p>
                    <Button
                      onClick={() => setActiveTab('match')}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      <Target className="w-4 h-4 mr-2" />
                      Match Your CV
                    </Button>
                  </div>
                </div>
              )}

              {/* Initial State */}
              {!searchResults && !loading && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-10 h-10 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Search for your dream job</h3>
                  <p className="text-slate-600 max-w-md mx-auto">
                    Enter keywords like "accountant", "driver", or "IT support" to find jobs across all major SA job boards.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Match Tab */}
          {activeTab === 'match' && (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  <Target className="w-5 h-5 inline mr-2 text-amber-500" />
                  Job Description Analyzer
                </h2>
                <p className="text-slate-600 text-sm mb-6">
                  Paste a job description and we'll analyze how well your CV matches, with specific suggestions to improve.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Select Your CV</label>
                    {cvs.length > 0 ? (
                      <Select value={selectedCV} onValueChange={setSelectedCV}>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Choose a CV" />
                        </SelectTrigger>
                        <SelectContent>
                          {cvs.map((cv) => (
                            <SelectItem key={cv.id} value={cv.id}>
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                {cv.title || cv.personal_details?.full_name || 'Untitled CV'}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="bg-slate-50 rounded-lg p-4 text-center">
                        <p className="text-slate-600 mb-2">No CVs found</p>
                        <Button size="sm" onClick={() => navigate('/cv-builder')}>
                          Create CV First
                        </Button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Job Description
                    </label>
                    <Textarea
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      placeholder="Paste the full job description here...

Example:
We are looking for a Sales Representative to join our team. Requirements:
- 2+ years sales experience
- Strong communication skills
- Valid driver's license
- Matric certificate"
                      className="min-h-[250px]"
                    />
                  </div>

                  <Button
                    onClick={handleMatch}
                    disabled={matchLoading || !selectedCV || !jobDescription.trim()}
                    className="w-full h-12 bg-amber-500 hover:bg-amber-600"
                  >
                    {matchLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Analyze Match
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Results */}
              <div>
                {matchResult ? (
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    {/* Score */}
                    <div className="text-center mb-6">
                      <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${getScoreColor(matchResult.ats_score)}`}>
                        <span className="text-3xl font-bold">{matchResult.ats_score}%</span>
                      </div>
                      <p className="text-slate-600 mt-2">ATS Compatibility Score</p>
                    </div>

                    {/* Keywords */}
                    {matchResult.keywords_found && (
                      <div className="mb-6">
                        <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          Matching Keywords
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {matchResult.keywords_found.map((kw, i) => (
                            <span key={i} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {matchResult.keywords_missing && matchResult.keywords_missing.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                          Missing Keywords (Add if true)
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {matchResult.keywords_missing.map((kw, i) => (
                            <span key={i} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggestions */}
                    {matchResult.suggestions && (
                      <div className="bg-slate-50 rounded-xl p-4">
                        <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-blue-500" />
                          Improvement Suggestions
                        </h4>
                        <div className="prose prose-sm text-slate-700">
                          {matchResult.suggestions.split('\n').map((line, i) => (
                            <p key={i} className="mb-2">{line}</p>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={() => navigate(`/cv-builder/${selectedCV}`)}
                      className="w-full mt-6 bg-slate-900 hover:bg-slate-800"
                    >
                      Edit CV to Improve Match
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  <div className="bg-slate-100 rounded-2xl p-8 text-center h-full flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                      <Target className="w-8 h-8 text-amber-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Match Results</h3>
                    <p className="text-slate-600 max-w-sm">
                      Paste a job description and click "Analyze Match" to see how well your CV fits the role.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default JobBoardPage;
